import { useCallback, useEffect, useRef } from 'react';
import { sendInput, type InputEvent, type MouseButton } from './protocol';

interface UseTouchTrackpadOpts {
  /** Surface that catches touch events. Usually the same div that hosts the <video>. */
  surface: HTMLElement | null;
  /** The actual <video> element — needed so the cursor overlay can be positioned inside the
   *  visible video bounds (accounting for `object-contain` letterbox) rather than the full
   *  surface which includes the black bars. */
  videoElRef?: React.RefObject<HTMLVideoElement | null>;
  /** Element to apply the zoom/pan transform to — typically the <video>. */
  zoomTargetRef?: React.RefObject<HTMLElement | null>;
  inputChannel: RTCDataChannel | null;
  enabled: boolean;
  cursorElRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Phone trackpad with pinch-to-zoom.
 *
 *   1 finger drag          → cursor move (relative, sensitivity-scaled)
 *   1 finger tap           → left click at virtual cursor
 *   1 finger long-press    → left button DOWN; lift → UP (drag-and-drop)
 *   2 fingers pinch/spread → zoom the video (1.0× – 4.0×) around the finger midpoint
 *   2 fingers parallel drag→ pan the zoomed video
 *
 * `syncCursor(x, y)` is exposed so the parent component can pin our internal cursor state to
 * whatever the host reports as its real OS mouse position (over the control DataChannel).
 */
export function useTouchTrackpad({
  surface,
  videoElRef,
  zoomTargetRef,
  inputChannel,
  enabled,
  cursorElRef,
}: UseTouchTrackpadOpts) {
  const channelRef = useRef(inputChannel);
  const enabledRef = useRef(enabled);

  // Two cursor positions, intentionally separate to avoid feedback-loop jitter:
  //
  //   - `cursorTargetRef`   = where we want the host mouse to go. Accumulated from finger
  //                           deltas with sensitivity scaling. Sent via `mouse_move` events.
  //                           Never read by the painter — only finger logic mutates it.
  //   - `cursorDisplayedRef`= where the host's OS mouse actually is, reported back over the
  //                           control channel ~30 Hz. The visible green crosshair tracks ONLY
  //                           this. Everything the user sees on the phone is therefore
  //                           guaranteed to match reality (with ~RTT lag), never local
  //                           prediction.
  //
  // When all fingers lift, we copy displayed → target so the next drag starts from the truthful
  // baseline (otherwise the target would drift further from reality on every gesture).
  const cursorTargetRef = useRef({ x: 0.5, y: 0.5 });
  const cursorDisplayedRef = useRef({ x: 0.5, y: 0.5 });

  // Per-touch state. Up to 2 fingers tracked.
  const touchesRef = useRef<
    Map<number, { startX: number; startY: number; lastX: number; lastY: number; startedAt: number }>
  >(new Map());

  const longPressTimerRef = useRef<number | null>(null);
  const longPressHeldRef = useRef(false);

  // Zoom/pan state — mutated directly through the ref so finger drags never re-render React.
  const transformRef = useRef({ scale: 1, panX: 0, panY: 0 });

  useEffect(() => {
    channelRef.current = inputChannel;
  }, [inputChannel]);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  /**
   * Compute the visible-video rectangle inside the surface, accounting for `object-contain`
   * letterbox bars. Used by both `paintCursor` (so the crosshair sits inside the video) and the
   * touch-delta normalisation (so finger motion in pixels maps to the host monitor at the same
   * rate horizontally and vertically — otherwise vertical drags feel half-speed on portrait
   * phones where the video occupies far less of the surface height than width).
   */
  const videoRect = useCallback(() => {
    if (!surface) return null;
    const video = videoElRef?.current;
    const sRect = surface.getBoundingClientRect();
    if (sRect.width === 0 || sRect.height === 0) return null;
    const vw = video?.videoWidth || 16;
    const vh = video?.videoHeight || 9;
    const videoAspect = vw / vh;
    const surfaceAspect = sRect.width / sRect.height;
    let visibleW: number;
    let visibleH: number;
    let offX: number;
    let offY: number;
    if (surfaceAspect > videoAspect) {
      visibleH = sRect.height;
      visibleW = sRect.height * videoAspect;
      offX = (sRect.width - visibleW) / 2;
      offY = 0;
    } else {
      visibleW = sRect.width;
      visibleH = sRect.width / videoAspect;
      offX = 0;
      offY = (sRect.height - visibleH) / 2;
    }
    return { offX, offY, visibleW, visibleH };
  }, [surface, videoElRef]);

  const paintCursor = useCallback(
    (x: number, y: number) => {
      const el = cursorElRef?.current;
      if (!el) return;
      const box = videoRect();
      if (!box) return;
      // Base pixel position inside the *untransformed* video rect.
      const baseX = box.offX + x * box.visibleW;
      const baseY = box.offY + y * box.visibleH;
      // The video element is `translate(panX, panY) scale(scale)` from origin (0,0). The cursor
      // overlay is its sibling in the same surface, so it has to honour the same transform —
      // otherwise zooming would visibly desync the crosshair from whatever pixel it's pointing
      // at on the host.
      const { scale, panX, panY } = transformRef.current;
      el.style.left = `${baseX * scale + panX}px`;
      el.style.top = `${baseY * scale + panY}px`;
    },
    [cursorElRef, videoRect],
  );

  const applyTransform = useCallback(() => {
    const el = zoomTargetRef?.current;
    if (el) {
      const { scale, panX, panY } = transformRef.current;
      el.style.transformOrigin = '0 0';
      el.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
    }
    // Re-paint the cursor with the new transform so it stays glued to the same host pixel as
    // the video moves under it. Use the local target during an active drag, the host-echo
    // position when idle (matches what `touchmove` / `syncCursor` would write).
    const ref = touchesRef.current.size > 0 ? cursorTargetRef.current : cursorDisplayedRef.current;
    paintCursor(ref.x, ref.y);
  }, [zoomTargetRef, paintCursor]);

  /**
   * Called by the parent component on every host-reported `cursor_position` control message.
   * Drives ONLY the visible green crosshair — the target used for outgoing `mouse_move` events
   * is rebaselined to the displayed truth only when no finger is currently touching the surface,
   * to avoid yanking the in-flight drag.
   */
  const syncCursor = useCallback(
    (x: number, y: number) => {
      const cx = clamp01(x);
      const cy = clamp01(y);
      cursorDisplayedRef.current.x = cx;
      cursorDisplayedRef.current.y = cy;
      // Only paint from echo when no finger is active. During a drag, local prediction owns the
      // visible cursor — stale RTT-old echo values would jitter it. As soon as the user lifts,
      // we resync target ← echo and paint, anchoring any accumulated drift.
      if (touchesRef.current.size === 0) {
        cursorTargetRef.current.x = cx;
        cursorTargetRef.current.y = cy;
        paintCursor(cx, cy);
      }
    },
    [paintCursor],
  );

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const sendMove = useCallback((x: number, y: number) => {
    const ev: InputEvent = { type: 'mouse_move', x: clamp01(x), y: clamp01(y) };
    sendInput(channelRef.current, ev);
  }, []);

  const sendClickAt = useCallback((button: MouseButton, x: number, y: number) => {
    sendInput(channelRef.current, { type: 'mouse_button', button, pressed: true, x, y });
    sendInput(channelRef.current, { type: 'mouse_button', button, pressed: false, x, y });
  }, []);

  // Exposed to the parent so on-screen LMB/RMB buttons can press and release at the *current*
  // virtual cursor position. Press-then-release while moving the cursor with the other hand is
  // the drag-and-drop primitive — separating press/release into discrete events makes that work.
  const sendMouseButton = useCallback((button: MouseButton, pressed: boolean) => {
    sendInput(channelRef.current, {
      type: 'mouse_button',
      button,
      pressed,
      x: cursorTargetRef.current.x,
      y: cursorTargetRef.current.y,
    });
  }, []);

  useEffect(() => {
    if (!surface) return;

    // Trackpad sensitivity — finger 1 px ⇒ this many normalised units across the *zoomed*
    // viewport. Divided by `scale` so when the user zooms in for precision, a finger drag of
    // the same length moves the cursor across fewer host pixels (~natural touchpad feel).
    // Trackpad sensitivity — 1 finger px ⇒ this many normalised host-monitor units, divided by
    // current zoom (so zoomed-in views feel slower / more precise). 1.6 ≈ stock laptop trackpad
    // feel — natural now that axis normalisation uses the visible-video rect (was effectively
    // double-counted on vertical drags before that fix).
    const SENSITIVITY_BASE = 1.6;
    const TAP_MAX_PX = 8;
    const TAP_MAX_MS = 250;
    const LONG_PRESS_MS = 500;
    // Two-finger drag at 1× zoom = scroll wheel. Notches per pixel of average finger travel.
    // Tuned so a ~150 px swipe ≈ one comfortable page-step; the host multiplies by 120 (Win) /
    // 3 lines (mac) per notch, and drops sub-1 raw deltas, so tiny jitters don't scroll.
    const SCROLL_SENS = 0.05;

    // Minimum scale we'll snap back to (preserves 1:1 default view).
    const SCALE_MIN = 1.0;
    const SCALE_MAX = 4.0;

    const surfaceRect = () => surface.getBoundingClientRect();

    const clampPan = () => {
      const { scale } = transformRef.current;
      const rect = surfaceRect();
      // With transform-origin 0,0 and translate-then-scale order, max pan is the slack between
      // surface width and the scaled element. We allow panX in [-(scale-1)*w, 0].
      const maxPanX = (scale - 1) * rect.width;
      const maxPanY = (scale - 1) * rect.height;
      transformRef.current.panX = clamp(-maxPanX, 0, transformRef.current.panX);
      transformRef.current.panY = clamp(-maxPanY, 0, transformRef.current.panY);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (!enabledRef.current) return;
      e.preventDefault();
      const now = performance.now();
      for (const t of Array.from(e.changedTouches)) {
        touchesRef.current.set(t.identifier, {
          startX: t.clientX,
          startY: t.clientY,
          lastX: t.clientX,
          lastY: t.clientY,
          startedAt: now,
        });
      }
      // Long-press only with exactly one finger down.
      if (touchesRef.current.size === 1) {
        cancelLongPress();
        longPressTimerRef.current = window.setTimeout(() => {
          longPressTimerRef.current = null;
          longPressHeldRef.current = true;
          sendInput(channelRef.current, {
            type: 'mouse_button',
            button: 'left',
            pressed: true,
            x: cursorTargetRef.current.x,
            y: cursorTargetRef.current.y,
          });
        }, LONG_PRESS_MS);
      } else {
        cancelLongPress();
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!enabledRef.current) return;
      e.preventDefault();
      const rect = surfaceRect();
      if (rect.width === 0 || rect.height === 0) return;

      if (touchesRef.current.size === 1 && e.touches.length === 1) {
        // ---- Single-finger drag → cursor move ----
        const t = e.touches[0];
        const prev = touchesRef.current.get(t.identifier);
        if (!prev) return;
        const dxPx = t.clientX - prev.lastX;
        const dyPx = t.clientY - prev.lastY;
        // Normalise by the visible *video* bounds, not the surface — otherwise vertical drags
        // would feel half-speed on a portrait phone where letterbox black bars eat ~50 % of the
        // surface height. With this, finger movement of N px maps to the same fraction of the
        // host monitor in either axis.
        const box = videoRect();
        const denomW = box?.visibleW ?? rect.width;
        const denomH = box?.visibleH ?? rect.height;
        const sens = SENSITIVITY_BASE / transformRef.current.scale;
        const dx = (dxPx / denomW) * sens;
        const dy = (dyPx / denomH) * sens;
        if (Math.hypot(t.clientX - prev.startX, t.clientY - prev.startY) > TAP_MAX_PX) {
          cancelLongPress();
        }
        cursorTargetRef.current.x = clamp01(cursorTargetRef.current.x + dx);
        cursorTargetRef.current.y = clamp01(cursorTargetRef.current.y + dy);
        prev.lastX = t.clientX;
        prev.lastY = t.clientY;
        sendMove(cursorTargetRef.current.x, cursorTargetRef.current.y);
        // Local prediction: paint the cursor at the target IMMEDIATELY so it feels 1:1 with the
        // finger. The echo path (`syncCursor`) deliberately won't repaint while a finger is
        // active, so we don't fight ourselves with stale RTT-old positions. When the user lifts,
        // echo takes over and snaps to the actual host cursor (anchors any accumulated drift).
        paintCursor(cursorTargetRef.current.x, cursorTargetRef.current.y);
      } else if (touchesRef.current.size === 2 && e.touches.length === 2) {
        // ---- Two-finger gesture → pinch zoom or pan, whichever dominates ----
        const a = e.touches[0];
        const b = e.touches[1];
        const pa = touchesRef.current.get(a.identifier);
        const pb = touchesRef.current.get(b.identifier);
        if (!pa || !pb) return;

        const prevDist = Math.hypot(pa.lastX - pb.lastX, pa.lastY - pb.lastY);
        const currDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const dDist = currDist - prevDist;
        const dxAvg = ((a.clientX - pa.lastX) + (b.clientX - pb.lastX)) / 2;
        const dyAvg = ((a.clientY - pa.lastY) + (b.clientY - pb.lastY)) / 2;
        const dPanLen = Math.hypot(dxAvg, dyAvg);

        if (Math.abs(dDist) > dPanLen) {
          // Pinch — scale around the finger midpoint (in surface coords).
          const factor = currDist / Math.max(prevDist, 1);
          const oldScale = transformRef.current.scale;
          const newScale = clamp(SCALE_MIN, SCALE_MAX, oldScale * factor);
          if (newScale !== oldScale) {
            const midSurfX = (a.clientX + b.clientX) / 2 - rect.left;
            const midSurfY = (a.clientY + b.clientY) / 2 - rect.top;
            // Adjust pan so the surface-point under the finger-midpoint stays put.
            // Pre-transform point: (midSurfX - panX) / oldScale.
            // After scale change we want: pre-point * newScale + newPanX == midSurfX
            // ⇒ newPanX = midSurfX - pre-point * newScale
            const preX = (midSurfX - transformRef.current.panX) / oldScale;
            const preY = (midSurfY - transformRef.current.panY) / oldScale;
            transformRef.current.scale = newScale;
            transformRef.current.panX = midSurfX - preX * newScale;
            transformRef.current.panY = midSurfY - preY * newScale;
            clampPan();
            applyTransform();
          }
        } else if (transformRef.current.scale > 1) {
          // Pan — only meaningful when zoomed in.
          transformRef.current.panX += dxAvg;
          transformRef.current.panY += dyAvg;
          clampPan();
          applyTransform();
        } else {
          // Two-finger drag at 1× zoom → scroll the remote (mouse wheel). Phone-native
          // "natural" direction: swipe up to read further down. Pan uses the same
          // content-follows-fingers sign convention, so the two gestures feel coherent.
          const wheelDy = -dyAvg * SCROLL_SENS;
          const wheelDx = -dxAvg * SCROLL_SENS;
          if (Math.abs(wheelDy) > 0.001 || Math.abs(wheelDx) > 0.001) {
            sendInput(channelRef.current, { type: 'wheel', dx: wheelDx, dy: wheelDy });
          }
        }

        pa.lastX = a.clientX;
        pa.lastY = a.clientY;
        pb.lastX = b.clientX;
        pb.lastY = b.clientY;
        cancelLongPress();
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!enabledRef.current) return;
      e.preventDefault();
      const now = performance.now();
      if (longPressHeldRef.current && touchesRef.current.size === 1 && e.touches.length === 0) {
        sendInput(channelRef.current, {
          type: 'mouse_button',
          button: 'left',
          pressed: false,
          x: cursorTargetRef.current.x,
          y: cursorTargetRef.current.y,
        });
        longPressHeldRef.current = false;
      }
      cancelLongPress();

      for (const t of Array.from(e.changedTouches)) {
        const prev = touchesRef.current.get(t.identifier);
        if (!prev) continue;
        const dt = now - prev.startedAt;
        const dPx = Math.hypot(t.clientX - prev.startX, t.clientY - prev.startY);
        if (!longPressHeldRef.current && dt < TAP_MAX_MS && dPx < TAP_MAX_PX) {
          const fingersAtStart = touchesRef.current.size;
          const button: MouseButton = fingersAtStart >= 2 ? 'right' : 'left';
          sendClickAt(button, cursorTargetRef.current.x, cursorTargetRef.current.y);
        }
        touchesRef.current.delete(t.identifier);
      }

      // If the user lifted everything and is back at scale 1.0, snap pan to 0 to avoid drift.
      if (e.touches.length === 0 && transformRef.current.scale <= 1.001) {
        transformRef.current.scale = 1;
        transformRef.current.panX = 0;
        transformRef.current.panY = 0;
        applyTransform();
      }
    };

    const onTouchCancel = onTouchEnd;

    surface.addEventListener('touchstart', onTouchStart, { passive: false });
    surface.addEventListener('touchmove', onTouchMove, { passive: false });
    surface.addEventListener('touchend', onTouchEnd, { passive: false });
    surface.addEventListener('touchcancel', onTouchCancel, { passive: false });
    return () => {
      surface.removeEventListener('touchstart', onTouchStart);
      surface.removeEventListener('touchmove', onTouchMove);
      surface.removeEventListener('touchend', onTouchEnd);
      surface.removeEventListener('touchcancel', onTouchCancel);
      cancelLongPress();
    };
  }, [surface, cancelLongPress, sendClickAt, sendMove, paintCursor, applyTransform, videoRect]);

  return { syncCursor, sendMouseButton };
}

function clamp01(v: number): number {
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

function clamp(min: number, max: number, v: number): number {
  if (v < min) return min;
  if (v > max) return max;
  return v;
}
